import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';

import { ApiService } from '../../core/services/api.service';
import { PmReportsApiService } from './pm-reports.service';
import {
  BurndownReportDto,
  BurnupReportDto,
  DeadlineDashboardDto,
  DeadlineReportRowDto,
  DistributionReportDto,
  EmployeeDelayPerformanceDto,
  PM_WIDGET_TYPES,
  PmDashboardDetailDto,
  PmDashboardDto,
  PmDashboardWidgetDto,
  PmSummaryDto,
  ProgressReportDto,
  ProjectDeadlineHealthDto,
  ResourceUtilizationReportDto,
  RiskReportDto,
  SprintReportDto,
  TeamWorkloadDto,
  TimeUtilizationReportDto,
  TrendReportDto,
  ThroughputReportDto,
  VelocityReportDto,
  WidgetDataDto,
  ProjectSlaDto,
  CumulativeFlowReportDto,
  CycleTimeReportDto,
  ForecastReportDto,
  BlockedIssueRowDto,
  MilestoneProgressReportDto,
  ReleaseProgressReportDto,
  ProjectCapacityDto,
  SprintHealthReportDto,
  ProductivityReportDto
} from './pm-reports.models';
import {
  deadlineBadgeClass,
  deadlineDisplayLabel,
  escalationClass,
  escalationLabel
} from '../shared/deadline.utils';
import {
  healthRagClass,
  velocityTrendLabel,
  velocityTrendClass,
  formatPercent,
  extractHealthMetrics
} from '../shared/health.utils';

interface ProjectRow { id: string; name: string; }
interface SprintRow { id: string; name: string; }

@Component({
  selector: 'app-pm-reports',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatFormFieldModule, MatSelectModule, MatInputModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, MatTabsModule, MatTableModule
  ],
  templateUrl: './pm-reports.component.html',
  styleUrl: './pm-reports.component.scss'
})
export class PmReportsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly reports = inject(PmReportsApiService);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly sprints = signal<SprintRow[]>([]);
  readonly selectedProjectId = signal<string | null>(null);
  readonly selectedSprintId = signal<string | null>(null);
  readonly summary = signal<PmSummaryDto | null>(null);
  readonly burndown = signal<BurndownReportDto | null>(null);
  readonly burnup = signal<BurnupReportDto | null>(null);
  readonly velocity = signal<VelocityReportDto | null>(null);
  readonly workload = signal<TeamWorkloadDto[]>([]);
  readonly dashboards = signal<PmDashboardDto[]>([]);
  readonly dashboard = signal<PmDashboardDetailDto | null>(null);
  readonly widgetData = signal<Record<string, WidgetDataDto | null>>({});
  readonly deadlineDash = signal<DeadlineDashboardDto | null>(null);
  readonly projectHealth = signal<ProjectDeadlineHealthDto | null>(null);
  readonly overdueRows = signal<DeadlineReportRowDto[]>([]);
  readonly delayRows = signal<DeadlineReportRowDto[]>([]);
  readonly employeePerf = signal<EmployeeDelayPerformanceDto[]>([]);

  readonly sprintReport = signal<SprintReportDto | null>(null);
  readonly progressReport = signal<ProgressReportDto | null>(null);
  readonly issueDistribution = signal<DistributionReportDto | null>(null);
  readonly priorityDistribution = signal<DistributionReportDto | null>(null);
  readonly bugTrend = signal<TrendReportDto | null>(null);
  readonly throughput = signal<ThroughputReportDto | null>(null);
  readonly timeUtilization = signal<TimeUtilizationReportDto | null>(null);
  readonly resourceUtilization = signal<ResourceUtilizationReportDto | null>(null);
  readonly riskReport = signal<RiskReportDto | null>(null);
  readonly loadingReports = signal(false);
  readonly projectSla = signal<ProjectSlaDto | null>(null);
  readonly cumulativeFlow = signal<CumulativeFlowReportDto | null>(null);
  readonly cycleTime = signal<CycleTimeReportDto | null>(null);
  readonly leadTime = signal<CycleTimeReportDto | null>(null);
  readonly forecast = signal<ForecastReportDto | null>(null);
  readonly blockedIssues = signal<BlockedIssueRowDto[]>([]);
  readonly milestoneProgress = signal<MilestoneProgressReportDto | null>(null);
  readonly releaseProgress = signal<ReleaseProgressReportDto | null>(null);
  readonly capacity = signal<ProjectCapacityDto | null>(null);
  readonly sprintHealth = signal<SprintHealthReportDto | null>(null);
  readonly productivity = signal<ProductivityReportDto | null>(null);
  readonly exporting = signal<string | null>(null);
  exportFormat: 'csv' | 'xlsx' = 'csv';

  dashboardName = '';
  widgetTitle = '';
  widgetType = 0;
  readonly widgetTypes = PM_WIDGET_TYPES;
  readonly workloadColumns = ['employeeName', 'openCount', 'inProgressCount', 'doneCount', 'remainingStoryPoints'];
  readonly reportColumns = ['keyOrTitle', 'statusLabel', 'assigneeName', 'dueDate', 'deadlineBadge', 'delayLabel', 'remainingLabel', 'escalation'];
  readonly blockedColumns = ['keyOrTitle', 'statusLabel', 'assigneeName', 'blockedSince', 'blockedReason', 'dueDate'];
  readonly milestoneColumns = ['name', 'targetDate', 'statusLabel', 'completionPercent', 'completedCount', 'versionName'];
  readonly releaseColumns = ['name', 'releaseDate', 'statusLabel', 'completionPercent', 'issueCount'];
  readonly capacityColumns = ['employeeName', 'openIssues', 'remainingStoryPoints', 'availableHours', 'leaveDays', 'isOverallocated'];
  readonly productivityColumns = ['employeeName', 'issuesCompleted', 'issuesOpen', 'storyPointsCompleted', 'hoursLogged', 'completionRatePercent', 'averageDelayDays', 'overdueTasks'];
  readonly perfColumns = ['employeeName', 'completedBeforeDeadline', 'completedOnTime', 'completedLate', 'averageDelayDays', 'overdueTasks', 'completionRatePercent'];
  readonly riskColumns = ['keyOrTitle', 'riskLevel', 'statusLabel', 'assigneeName', 'dueDate'];
  readonly resourceColumns = ['employeeName', 'openIssues', 'remainingStoryPoints', 'availableHours', 'isOverallocated'];

  ngOnInit(): void {
    const queryProjectId = this.route.snapshot.queryParamMap.get('projectId');
    this.api.get<{ items?: ProjectRow[] } | ProjectRow[]>('v1/projects', { take: 100 }).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : (res.items ?? []);
        this.projects.set(items);
        if (items.length) {
          const initial = queryProjectId && items.some(p => p.id === queryProjectId)
            ? queryProjectId
            : items[0].id;
          this.selectedProjectId.set(initial);
          this.reloadAll();
        }
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  onProjectChange(id: string): void {
    this.selectedProjectId.set(id);
    this.reloadAll();
  }

  onSprintChange(id: string): void {
    this.selectedSprintId.set(id);
    this.loadCharts();
    this.loadSprintReport();
    this.loadSprintHealth();
  }

  reloadAll(): void {
    const projectId = this.selectedProjectId();
    if (!projectId) return;
    this.loading.set(true);
    this.error.set(null);

    this.reports.summary(projectId).subscribe({
      next: (s) => this.summary.set(s),
      error: () => this.summary.set(null)
    });
    this.reports.velocity(projectId).subscribe({
      next: (v) => this.velocity.set(v),
      error: () => this.velocity.set(null)
    });
    this.reports.workload(projectId).subscribe({
      next: (w) => this.workload.set(Array.isArray(w) ? w : []),
      error: () => this.workload.set([])
    });
    this.reports.listDashboards(projectId).subscribe({
      next: (d) => this.dashboards.set(Array.isArray(d) ? d : []),
      error: () => this.dashboards.set([])
    });

    this.reports.deadlineDashboard(projectId).subscribe({
      next: (d) => this.deadlineDash.set(d),
      error: () => this.deadlineDash.set(null)
    });
    this.reports.projectHealth(projectId).subscribe({
      next: (h) => this.projectHealth.set(h),
      error: () => this.projectHealth.set(null)
    });
    this.reports.overdueReport(projectId).subscribe({
      next: (r) => this.overdueRows.set(Array.isArray(r) ? r : []),
      error: () => this.overdueRows.set([])
    });
    this.reports.delayReport(projectId).subscribe({
      next: (r) => this.delayRows.set(Array.isArray(r) ? r : []),
      error: () => this.delayRows.set([])
    });
    this.reports.employeePerformance(projectId).subscribe({
      next: (r) => this.employeePerf.set(Array.isArray(r) ? r : []),
      error: () => this.employeePerf.set([])
    });

    this.reports.projectSla(projectId).subscribe({
      next: (s) => this.projectSla.set(s),
      error: () => this.projectSla.set(null)
    });

    this.loadExtendedReports(projectId);

    this.api.get<SprintRow[] | { items?: SprintRow[] }>('v1/scrum/sprints', { projectId }).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : (res.items ?? []);
        this.sprints.set(items);
        this.selectedSprintId.set(items[0]?.id ?? null);
        this.loadCharts();
        this.loadSprintReport();
        this.loadSprintHealth();
        this.loading.set(false);
      },
      error: () => {
        this.sprints.set([]);
        this.loading.set(false);
      }
    });
  }

  loadExtendedReports(projectId: string): void {
    this.loadingReports.set(true);
    this.reports.progressReport(projectId).subscribe({
      next: (r) => this.progressReport.set(r),
      error: () => this.progressReport.set(null)
    });
    this.reports.issueDistribution(projectId).subscribe({
      next: (r) => this.issueDistribution.set(r),
      error: () => this.issueDistribution.set(null)
    });
    this.reports.priorityDistribution(projectId).subscribe({
      next: (r) => this.priorityDistribution.set(r),
      error: () => this.priorityDistribution.set(null)
    });
    this.reports.bugTrend(projectId).subscribe({
      next: (r) => this.bugTrend.set(r),
      error: () => this.bugTrend.set(null)
    });
    this.reports.throughput(projectId).subscribe({
      next: (r) => this.throughput.set(r),
      error: () => this.throughput.set(null)
    });
    this.reports.timeUtilization(projectId).subscribe({
      next: (r) => this.timeUtilization.set(r),
      error: () => this.timeUtilization.set(null)
    });
    this.reports.resourceUtilization(projectId).subscribe({
      next: (r) => this.resourceUtilization.set(r),
      error: () => this.loadingReports.set(false)
    });
    this.reports.riskReport(projectId).subscribe({
      next: (r) => { this.riskReport.set(r); this.loadingReports.set(false); },
      error: () => this.loadingReports.set(false)
    });
    this.reports.cumulativeFlow(projectId).subscribe({
      next: (r) => this.cumulativeFlow.set(r),
      error: () => this.cumulativeFlow.set(null)
    });
    this.reports.cycleTime(projectId).subscribe({
      next: (r) => this.cycleTime.set(r),
      error: () => this.cycleTime.set(null)
    });
    this.reports.leadTime(projectId).subscribe({
      next: (r) => this.leadTime.set(r),
      error: () => this.leadTime.set(null)
    });
    this.reports.forecast(projectId).subscribe({
      next: (r) => this.forecast.set(r),
      error: () => this.forecast.set(null)
    });
    this.reports.blockedIssues(projectId).subscribe({
      next: (r) => this.blockedIssues.set(Array.isArray(r) ? r : []),
      error: () => this.blockedIssues.set([])
    });
    this.reports.milestoneProgress(projectId).subscribe({
      next: (r) => this.milestoneProgress.set(r),
      error: () => this.milestoneProgress.set(null)
    });
    this.reports.releaseProgress(projectId).subscribe({
      next: (r) => this.releaseProgress.set(r),
      error: () => this.releaseProgress.set(null)
    });
    this.reports.capacity(projectId).subscribe({
      next: (r) => this.capacity.set(r),
      error: () => this.capacity.set(null)
    });
    this.reports.productivity(projectId).subscribe({
      next: (r) => this.productivity.set(r),
      error: () => this.productivity.set(null)
    });
  }

  loadSprintHealth(): void {
    const projectId = this.selectedProjectId();
    const sprintId = this.selectedSprintId();
    if (!projectId || !sprintId) {
      this.sprintHealth.set(null);
      return;
    }
    this.reports.sprintHealth(projectId, sprintId).subscribe({
      next: (r) => this.sprintHealth.set(r),
      error: () => this.sprintHealth.set(null)
    });
  }

  loadSprintReport(): void {
    const projectId = this.selectedProjectId();
    const sprintId = this.selectedSprintId();
    if (!projectId || !sprintId) {
      this.sprintReport.set(null);
      return;
    }
    this.reports.sprintReport(projectId, sprintId).subscribe({
      next: (r) => this.sprintReport.set(r),
      error: () => this.sprintReport.set(null)
    });
  }

  loadCharts(): void {
    const projectId = this.selectedProjectId();
    const sprintId = this.selectedSprintId();
    if (!projectId || !sprintId) {
      this.burndown.set(null);
      this.burnup.set(null);
      return;
    }
    this.reports.burndown(projectId, sprintId).subscribe({
      next: (b) => this.burndown.set(b),
      error: () => this.burndown.set(null)
    });
    this.reports.burnup(projectId, sprintId).subscribe({
      next: (b) => this.burnup.set(b),
      error: () => this.burnup.set(null)
    });
  }

  createDashboard(): void {
    if (!this.dashboardName.trim()) return;
    this.reports.createDashboard({
      name: this.dashboardName.trim(),
      projectId: this.selectedProjectId() || undefined,
      isDefault: true
    }).subscribe({
      next: (d) => {
        this.dashboardName = '';
        this.reloadAll();
        this.openDashboard(d.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to create dashboard.')
    });
  }

  createRolePresets(): void {
    this.reports.ensureRoleDashboards(this.selectedProjectId() || undefined).subscribe({
      next: (dashboards) => {
        this.reloadAll();
        const shared = (Array.isArray(dashboards) ? dashboards : []).filter(d => d.isShared);
        const first = shared[0] ?? (Array.isArray(dashboards) ? dashboards[0] : null);
        if (first?.id) this.openDashboard(first.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to create role presets.')
    });
  }

  openDashboard(id: string): void {
    this.widgetData.set({});
    this.reports.getDashboard(id).subscribe({
      next: (d) => {
        this.dashboard.set(d);
        (d.widgets ?? []).forEach(w => this.loadWidgetData(d.id, w));
      },
      error: (err) => this.error.set(err?.message || 'Failed to load dashboard.')
    });
  }

  loadWidgetData(dashboardId: string, widget: PmDashboardWidgetDto): void {
    const cached = this.resolveWidgetFromCache(widget);
    if (cached) {
      this.widgetData.update(m => ({ ...m, [widget.id]: cached }));
      return;
    }
    const params: Record<string, string | number> = {};
    if (this.selectedProjectId()) params['projectId'] = this.selectedProjectId()!;
    if (this.selectedSprintId()) params['sprintId'] = this.selectedSprintId()!;
    this.reports.getWidgetData(dashboardId, widget.id, params).subscribe({
      next: (data) => this.widgetData.update(m => ({ ...m, [widget.id]: data })),
      error: () => this.widgetData.update(m => ({ ...m, [widget.id]: this.resolveWidgetFromCache(widget) }))
    });
  }

  resolveWidgetFromCache(widget: PmDashboardWidgetDto): WidgetDataDto | null {
    switch (widget.widgetType) {
      case 0: return this.summary() ? { widgetType: 0, summary: this.summary()! } : null;
      case 1: return this.burndown() ? { widgetType: 1, burndown: this.burndown()! } : null;
      case 2: return this.burnup() ? { widgetType: 2, burnup: this.burnup()! } : null;
      case 3: return this.velocity() ? { widgetType: 3, velocity: this.velocity()! } : null;
      case 4: return this.workload().length ? { widgetType: 4, workload: this.workload() } : null;
      case 6: return this.deadlineDash() ? { widgetType: 6, deadlineDashboard: this.deadlineDash()! } : null;
      case 7: return this.overdueRows().length ? { widgetType: 7, overdueRows: this.overdueRows() } : null;
      case 8: return this.projectHealth() ? { widgetType: 8, projectHealth: this.projectHealth()! } : null;
      default: return null;
    }
  }

  addWidget(): void {
    const dash = this.dashboard();
    if (!dash || !this.widgetTitle.trim()) return;
    const config = JSON.stringify({
      projectId: this.selectedProjectId(),
      sprintId: this.selectedSprintId()
    });
    this.reports.addWidget(dash.id, {
      title: this.widgetTitle.trim(),
      widgetType: this.widgetType,
      configJson: config
    }).subscribe({
      next: () => { this.widgetTitle = ''; this.openDashboard(dash.id); },
      error: (err) => this.error.set(err?.message || 'Failed to add widget.')
    });
  }

  deleteWidget(widgetId: string): void {
    const dash = this.dashboard();
    if (!dash || !confirm('Delete this widget?')) return;
    this.reports.deleteWidget(dash.id, widgetId).subscribe({
      next: () => this.openDashboard(dash.id),
      error: (err) => this.error.set(err?.message || 'Failed to delete widget.')
    });
  }

  deleteDashboard(id: string): void {
    this.reports.deleteDashboard(id).subscribe({
      next: () => { this.dashboard.set(null); this.reloadAll(); },
      error: (err) => this.error.set(err?.message || 'Failed to delete dashboard.')
    });
  }

  maxPoint(points: { value: number }[] | undefined): number {
    if (!points?.length) return 1;
    return Math.max(...points.map(p => p.value), 1);
  }

  throughputBarHeight(value: number, weekly: { completedIssues: number }[]): number {
    const max = Math.max(...(weekly?.map(w => w.completedIssues) ?? [0]), 1);
    return (value / max) * 100;
  }

  maxCount(items: { count: number }[] | undefined): number {
    if (!items?.length) return 1;
    return Math.max(...items.map(i => i.count), 1);
  }

  refreshDeadlines(): void {
    const projectId = this.selectedProjectId() || undefined;
    this.reports.refreshDeadlines(projectId).subscribe({
      next: () => this.reloadAll(),
      error: (err) => this.error.set(err?.message || 'Failed to refresh deadlines.')
    });
  }

  widgetTypeLabel(type: number): string {
    return this.widgetTypes.find(t => t.value === type)?.label ?? `Type ${type}`;
  }

  widgetDataFor(id: string): WidgetDataDto | null {
    return this.widgetData()[id] ?? null;
  }

  maxHours(total: number | undefined, itemHours: number): number {
    return Math.max(total ?? itemHours, 1);
  }

  exportCsv(type: string): void {
    const projectId = this.selectedProjectId();
    if (!projectId || this.exporting()) return;
    this.exporting.set(type);
    const sprintId = this.selectedSprintId() || undefined;
    this.reports.exportReport(projectId, type, this.exportFormat, sprintId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-report-${projectId}.${this.exportFormat === 'xlsx' ? 'xlsx' : 'csv'}`;
        a.click();
        URL.revokeObjectURL(url);
        this.exporting.set(null);
      },
      error: () => this.exporting.set(null)
    });
  }

  cfdStatusLabels(): string[] {
    return ['To Do', 'In Progress', 'Done'];
  }

  cfdCount(day: { toDo?: number; inProgress?: number; done?: number; statuses?: { label: string; count: number }[] }, label: string): number {
    if (day.statuses?.length) return day.statuses.find(s => s.label === label)?.count ?? 0;
    if (label === 'To Do') return day.toDo ?? 0;
    if (label === 'In Progress') return day.inProgress ?? 0;
    if (label === 'Done') return day.done ?? 0;
    return 0;
  }

  cfdMax(day: { toDo?: number; inProgress?: number; done?: number; statuses?: { label: string; count: number }[] }): number {
    if (day.statuses?.length) return Math.max(...day.statuses.map(s => s.count), 1);
    return Math.max(day.toDo ?? 0, day.inProgress ?? 0, day.done ?? 0, 1);
  }

  /** Build SVG polyline points for chart series. */
  svgLine(points: { label: string; value: number }[] | undefined, width = 320, height = 140, pad = 16): string {
    if (!points?.length) return '';
    const max = Math.max(...points.map(p => Number(p.value) || 0), 1);
    const n = points.length;
    return points.map((p, i) => {
      const x = pad + (n === 1 ? width / 2 : (i / (n - 1)) * (width - pad * 2));
      const y = height - pad - ((Number(p.value) || 0) / max) * (height - pad * 2);
      return `${x},${y}`;
    }).join(' ');
  }

  svgCfdLine(
    points: { date: string; toDo: number; inProgress: number; done: number }[] | undefined,
    key: 'toDo' | 'inProgress' | 'done',
    width = 360,
    height = 160,
    pad = 16
  ): string {
    if (!points?.length) return '';
    const max = Math.max(...points.map(p => Math.max(p.toDo, p.inProgress, p.done)), 1);
    const n = points.length;
    return points.map((p, i) => {
      const x = pad + (n === 1 ? width / 2 : (i / (n - 1)) * (width - pad * 2));
      const y = height - pad - (p[key] / max) * (height - pad * 2);
      return `${x},${y}`;
    }).join(' ');
  }

  svgVelocityBars(sprints: { name: string; committedPoints: number; completedPoints: number }[] | undefined): {
    x: number; committedH: number; completedH: number; label: string;
  }[] {
    if (!sprints?.length) return [];
    const max = Math.max(...sprints.flatMap(s => [s.committedPoints, s.completedPoints]), 1);
    const chartH = 120;
    return sprints.map((s, i) => ({
      x: 24 + i * 48,
      committedH: (s.committedPoints / max) * chartH,
      completedH: (s.completedPoints / max) * chartH,
      label: s.name
    }));
  }

  badgeClass = deadlineBadgeClass;
  rowDeadlineLabel = deadlineDisplayLabel;
  escalationClass = escalationClass;
  escalationLabel = escalationLabel;
  healthRagClass = healthRagClass;
  velocityTrendLabel = velocityTrendLabel;
  velocityTrendClass = velocityTrendClass;
  formatPercent = formatPercent;
  extractHealthMetrics = extractHealthMetrics;
}
